import {
    ParticipantAccountStatus,
    type ParticipantAccountStatus as ParticipantAccountStatusValue,
    type RetailEnrollmentErrorCode
} from "@solomon-apps/shared";

import { type RetailEnrollmentLocalErrorCode, RetailEnrollmentRequestError } from "@/hooks/yaas/useRetailEnrollment";
import { WalletVerificationError } from "@/services/yaas/walletVerificationError";

export type EnrollmentFailureCode = RetailEnrollmentErrorCode | RetailEnrollmentLocalErrorCode;

export interface EnrollmentFlowFailure {
    code: EnrollmentFailureCode;
    message: string;
    displayMessage: string;
    status: number | null;
    requestId: string | null;
    retryAfterSeconds: number | null;
    canRetryWithMicroTransfer: boolean;
    retryWithNewChallenge?: boolean;
    hasTransferProof?: boolean;
    retryMethod?: "microTransfer";
    /** The user declined the verification transfer in their wallet. Not an
     *  error to explain: the form shows it on the button alone, then clears
     *  it back to the start. */
    canceled?: boolean;
}

export type EnrollmentFailureAction = "keep-form" | "reset-terms" | "account-unavailable";

const ACCOUNT_STATUS_BY_CODE: Partial<Record<EnrollmentFailureCode, Exclude<ParticipantAccountStatusValue, "Active">>> =
    {
        account_pending: ParticipantAccountStatus.Pending,
        account_suspended: ParticipantAccountStatus.Suspended,
        account_blacklisted: ParticipantAccountStatus.Blacklisted,
        account_closed: ParticipantAccountStatus.Closed
    };

const COPY_BY_CODE: Record<EnrollmentFailureCode, string> = {
    validation_failed: "Check the form values and try again.",
    payload_mismatch: "Your enrollment details changed. Start a new verification.",
    signature_invalid: "The wallet signature was not valid. Start a new verification.",
    verification_invalid: "The verification transfer did not match this request. Start a new verification.",
    verification_pending: "The transfer is awaiting confirmation. Retry without sending another transfer.",
    transfer_failed: "The verification transfer failed. Check your wallet before trying again.",
    transfer_unavailable: "Small-transfer verification is unavailable for this request. Start a new verification.",
    terms_revision_mismatch: "The terms changed. Review and accept the current terms.",
    nonce_expired: "The verification request expired. Start a new verification.",
    nonce_consumed: "The verification request is no longer valid. Start a new verification.",
    account_pending: "This account is not available for enrollment.",
    account_suspended: "This account is not available for enrollment.",
    account_blacklisted: "This account is not available for enrollment.",
    account_closed: "This account is not available for enrollment.",
    rate_limited: "Verification requests are temporarily limited. Please wait up to 5 minutes before trying again.",
    internal_error: "Enrollment could not finish. Start a new verification.",
    enrollment_unavailable: "Enrollment is temporarily unavailable. Try again later.",
    network_error: "The network request failed. Start a new verification.",
    malformed_response: "The enrollment service returned an invalid response. Start a new verification.",
    wallet_rejected: "You canceled the wallet signature request.",
    wallet_mismatch: "The connected wallet changed. Start again with the current wallet.",
    unsupported_signing: "Your wallet does not support message signing.",
    signing_failed: "We couldn’t sign the verification message.",
    signing_timeout:
        "Your wallet did not respond to the signing request. Close the pending wallet request before trying again."
};

export function enrollmentFailureAction(code: EnrollmentFailureCode): EnrollmentFailureAction {
    if (ACCOUNT_STATUS_BY_CODE[code]) return "account-unavailable";
    return code === "terms_revision_mismatch" ? "reset-terms" : "keep-form";
}

export function enrollmentFailureCopy(code: EnrollmentFailureCode): string {
    return COPY_BY_CODE[code];
}

export function accountStatusFromEnrollmentError(
    code: EnrollmentFailureCode
): Exclude<ParticipantAccountStatusValue, "Active"> | null {
    return ACCOUNT_STATUS_BY_CODE[code] ?? null;
}

export function toFlowFailure(error: unknown): EnrollmentFlowFailure {
    if (error instanceof RetailEnrollmentRequestError || error instanceof WalletVerificationError) {
        return {
            code: error.code,
            message: error.message,
            displayMessage: enrollmentFailureCopy(error.code),
            status: error.status,
            requestId: error.requestId,
            retryAfterSeconds: error.retryAfterSeconds,
            canRetryWithMicroTransfer: false
        };
    }
    return {
        code: "internal_error",
        message: error instanceof Error ? error.message : "Enrollment failed",
        displayMessage: enrollmentFailureCopy("internal_error"),
        status: null,
        requestId: null,
        retryAfterSeconds: null,
        canRetryWithMicroTransfer: false
    };
}

export interface EnrollmentRetryOptions {
    preferredMethod?: "microTransfer";
    restartVerification?: boolean;
}

/** Select the verification method and proof recovery policy for a manual retry. */
export function enrollmentRetryOptions(failure: EnrollmentFlowFailure | null): EnrollmentRetryOptions {
    if (failure?.retryWithNewChallenge) {
        return { preferredMethod: "microTransfer", restartVerification: true };
    }
    const signingFailureOffersTransfer =
        failure?.canRetryWithMicroTransfer === true &&
        (failure.code === "signature_invalid" ||
            failure.code === "unsupported_signing" ||
            failure.code === "signing_failed" ||
            failure.code === "signing_timeout" ||
            failure.code === "transfer_failed");
    if (failure?.hasTransferProof || failure?.retryMethod === "microTransfer" || signingFailureOffersTransfer) {
        return { preferredMethod: "microTransfer" };
    }
    return {};
}
